import {
  Box,
  Button,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { formatDateAndTimeFromString } from "../../utils/date-setup";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";

import ShortcutIcon from "@mui/icons-material/Shortcut";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import { useNotificationService } from "../../hooks/notification";
type Comment = {
  id: number; // Changed from string to number to match your format
  parent_id: number | null; // Using snake_case to match server format
  post_id: number; // Added post_id from your structure
  author_id: number; // Added to track the comment author
  author_name: string; // Changed from author to author_name
  author_photo_url: string; // Added author photo
  comment: string; // Changed from content to comment
  likes: number; // Added likes count
  commented_at: string; // Changed from createdAt to commented_at
  total_replies: number; // Kept total_replies
  replies?: Comment[]; // Optional replies array for nested structure
};
type CommentProps = {
  comment: Comment;
  onLoadReplies: (commentId: number) => Promise<void>;
  onAddReply: (
    parentId: number,
    comment: string,
    postId: number
  ) => Promise<void>;
  onLike?: (commentId: number) => Promise<void>; // Added like functionality
  isExpanded: boolean;
  isLoading: boolean;
  level?: number;
};

export default function Comment({
  comment,
  onLoadReplies,
  onAddReply,
  onLike,
  isExpanded,
  isLoading,
  level = 0,
}: CommentProps) {
  // State for managing the reply input form
  const [replyContent, setReplyContent] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const account_profile_data = useSelector(
    (state: RootState) => state.content.account_profile_data
  );
  const { showNotification } = useNotificationService();
  // Handle submitting a new reply
  const handleSubmitReply = async () => {
    if (!replyContent.trim()) {
      showNotification("Kindly input reply", "warning");
      return;
    }

    // Call the parent component's handler with all necessary data
    await onAddReply(comment.id, replyContent, comment.post_id);

    // Reset the form state after successful submission
    setReplyContent("");
    setIsReplying(false);
  };

  return (
    <Box
      style={{ marginLeft: `${level * 20}px` }}
      sx={{ mt: 4, pl: 1, borderLeft: level != 0 ? "1px solid #ccc" : "" }}
    >
      {/* Comment header with author information */}
      <Box>
        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={"space-between"}
          spacing={2}
        >
          <Stack direction="row" alignItems={"center"} spacing={2}>
            <img
              src={comment.author_photo_url}
              alt="Author"
              style={{ width: 30, height: 30, borderRadius: "50%" }}
            />
            <Stack>
              <Typography variant="subtitle2" color="black">
                {comment.author_name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#4A5E6D", fontWeight: 400 }}
              >
                {formatDateAndTimeFromString(comment.commented_at)}
              </Typography>
            </Stack>
          </Stack>
          {/* Comment actions */}
        {level === 0 &&  <Box className="comment-content">
            <Stack direction="row" spacing={1}>
              <Button onClick={() => setIsReplying(!isReplying)}>
                <ShortcutIcon />
                Reply
              </Button>
              <Button
                onClick={() => onLike?.(comment.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 16,
                }}
              >
                <ThumbUpAltIcon />{" "}
                <span style={{ margin: "0 8px" }}>{comment.likes} </span>
              </Button>
              {/* Show replies button */}
              {comment.total_replies > 0 && !isExpanded && (
                <Button
                  onClick={() => onLoadReplies(comment.id)}
                  disabled={isLoading}
                  variant="contained"
                >
                  {isLoading
                    ? "Loading..."
                    : `Show ${
                        comment.total_replies === 1 ? "reply" : "replies"
                      }`}
                </Button>
              )}
            </Stack>
          </Box>}
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ ml: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {comment.comment}
          </Typography>
        </Box>
        <Box>
          {/* Reply form */}
          {isReplying && (
            <Box sx={{ my: 2 }}>
              <TextField
                value={replyContent}
                multiline={true}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Write a reply..."
                rows={1}
                size="small"
                sx={{ width: 300 }}
              />
              <Stack direction={"row"}>
                <Button onClick={() => setIsReplying(false)}>Cancel</Button>
                <Button
                  onClick={handleSubmitReply}
                  disabled={!replyContent.trim()}
                >
                  Submit Reply
                </Button>
              </Stack>
            </Box>
          )}

          {/* Nested replies */}
          {isExpanded && comment.replies && comment.replies.length > 0 && (
            <div className="replies mt-4">
              {comment.replies.map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  onLoadReplies={onLoadReplies}
                  onAddReply={onAddReply}
                  onLike={onLike}
                  isExpanded={isExpanded}
                  isLoading={isLoading}
                  level={level + 1}
                />
              ))}
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
}
